import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, money, money0, shortDate, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { KpiCard, StatusPill, RowActions } from "../components/UI";
import { useAuth } from "../lib/auth";
import { useSortable, unitNumberKey } from "../lib/sortable";
import { can } from "../lib/roles";
import { MessageSquare, Trash2 } from "lucide-react";

function useSafeBackdrop(onClose) {
  const [downOnBackdrop, setDownOnBackdrop] = React.useState(false);
  return {
    onMouseDown: (e) => setDownOnBackdrop(e.target === e.currentTarget),
    onMouseUp: (e) => {
      if (downOnBackdrop && e.target === e.currentTarget) onClose();
      setDownOnBackdrop(false);
    },
    stop: {
      onMouseDown: (e) => e.stopPropagation(),
      onMouseUp: (e) => e.stopPropagation(),
    },
  };
}

// OffHireDialog serves two flows:
//  - Fresh off-hire (hire.status === 'active'): pick end date, optional demob charge,
//    same UX as before, but now shows the trim/cancel preview from the server so
//    stand-downs and future one-offs to be clipped are called out explicitly.
//  - Amendment (hire.status === 'ended' with actual_end_date set): change the
//    existing off-hire date. Demob input is hidden — demob belongs to the original
//    off-hire, not the correction.
// The preview is fetched from POST /off-hire/preview (server-side, uses the same
// helper as the mutating endpoint so the numbers match). If any affected month
// has posted to Xero the SERVER refuses on save; we still show the preview so the
// user can see what would have happened.
function OffHireDialog({ hire, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const isAmendment = !!hire.actual_end_date || hire.status === "ended";
  const [endDate, setEndDate] = useState(isAmendment ? (hire.actual_end_date || today) : today);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirmBefore, setConfirmBefore] = useState(false);
  const [addDemob, setAddDemob] = useState(false);
  const [demob, setDemob] = useState({ description: "Demobilisation", rate: "", qty: 1 });
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  const [acknowledge, setAcknowledge] = useState(false);
  // 7 Sep 2026: user-driven keep/cancel per future one-off charge. Default = empty set
  // (cancel everything, same as before). Ticking a box adds the id to this set,
  // and the confirm request includes charges_to_keep with those ids.
  const [keepIds, setKeepIds] = useState(() => new Set());
  // Restored 1 Oct 2026: edit amount of each pending bill_at_hire_end charge
  // before it bills. Map of one_off_charge.id -> string (free-text input).
  // Reconstructed from main.9ac05cdf.js (deployed 00:12 UTC) after a
  // frontend regression dropped it between that build and the next one. Backend
  // API /off-hire still accepts demob_amend_amounts and the server-side
  // off-hire preview still returns pending_bill_at_hire_end — only the UI
  // layer was lost.
  const [demobAmends, setDemobAmends] = useState({});
  const backdrop = useSafeBackdrop(onClose);
  const isBeforeStart = endDate && hire.start_date && endDate < hire.start_date;
  const hasChangedFromExisting = !isAmendment || endDate !== hire.actual_end_date;

  // Debounced preview refresh whenever endDate changes.
  useEffect(() => {
    if (!endDate) { setPreview(null); return; }
    let cancelled = false;
    setPreviewErr("");
    const t = setTimeout(async () => {
      try {
        const r = await api.post(`/hires/${hire.id}/off-hire/preview`,
          { end_date: endDate, force: !!confirmBefore });
        if (!cancelled) setPreview(r.data);
      } catch (e) {
        if (!cancelled) { setPreview(null); setPreviewErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [endDate, hire.id, confirmBefore]);

  const trimsOrCancels = (preview?.sd_trims?.length || 0)
                       + (preview?.one_off_cancels?.length || 0)
                       + (preview?.custom_charge_cancels?.length || 0);
  const needsAck = trimsOrCancels > 0;

  const save = async () => {
    if (isBeforeStart && !confirmBefore) {
      setErr(`That off-hire date is before the hire started (${hire.start_date}). Tick the box to confirm.`);
      return;
    }
    if (needsAck && !acknowledge) {
      setErr("Please tick the confirmation box below — this off-hire will trim or cancel some entries.");
      return;
    }
    setBusy(true); setErr("");
    try {
      const body = {
        end_date: endDate,
        force: !!confirmBefore,
        charges_to_keep: Array.from(keepIds),
      };
      // Build demob_amend_amounts from any non-empty edits that differ from the
      // charge's current amount. Empty string == "no change", explicit value
      // overrides the stored rate at off-hire time.
      const pending = preview?.pending_bill_at_hire_end || [];
      const amends = {};
      for (const row of pending) {
        const raw = demobAmends[row.id];
        if (raw === undefined || raw === null || raw === "") continue;
        const num = Number(raw);
        if (Number.isFinite(num) && num !== Number(row.current_amount)) {
          amends[row.id] = num;
        }
      }
      if (Object.keys(amends).length > 0) body.demob_amend_amounts = amends;
      if (!isAmendment && addDemob && demob.description.trim() && Number(demob.rate) > 0) {
        body.demob_charge = {
          description: demob.description.trim(),
          rate: Number(demob.rate),
          qty: Number(demob.qty || 1),
          kind: "one-off",
          one_off_month: endDate.slice(0, 7),
        };
      }
      await api.post(`/hires/${hire.id}/off-hire`, body);
      onSaved(); onClose();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); setBusy(false); }
  };
  return (
    <div className="ach-modal-backdrop" data-testid="off-hire-dialog" onMouseDown={backdrop.onMouseDown} onMouseUp={backdrop.onMouseUp}>
      <div className="ach-modal" style={{maxWidth: 560}} {...backdrop.stop}>
        <div className="ach-modal__header"><h2 style={{margin:0,fontSize:16,fontWeight:600}}>
          {isAmendment ? `Adjust off-hire date · ${hire.machine?.unit_number}` : `Off-hire ${hire.machine?.unit_number}?`}
        </h2></div>
        <div className="ach-modal__body">
          <p style={{margin:"0 0 12px", fontSize:13, color:"var(--ach-text-1)"}}>
            {isAmendment ? (
              <>Change the day this machine came off hire. Current off-hire date is <strong>{hire.actual_end_date}</strong>.</>
            ) : (
              <>Pick the day the machine actually came off hire. Hire started <strong>{hire.start_date}</strong>.</>
            )}
          </p>
          <div className="ach-field">
            <label>{isAmendment ? "New off-hire date" : "Off-hire date"}</label>
            <input type="date" value={endDate} onChange={(e)=>{setEndDate(e.target.value); setConfirmBefore(false); setAcknowledge(false); setKeepIds(new Set());}} data-testid="off-hire-date" />
          </div>
          {isBeforeStart && (
            <label style={{display:"flex", gap:8, alignItems:"flex-start", marginTop:10, padding:10, background:"var(--ach-amber-tint)", border:"1px solid var(--ach-amber)", borderRadius:6, fontSize:12}}>
              <input type="checkbox" checked={confirmBefore} onChange={(e)=>setConfirmBefore(e.target.checked)} data-testid="off-hire-confirm-before" style={{marginTop:2}} />
              <span>This off-hire date is <strong>before</strong> the hire started ({hire.start_date}). Tick to confirm you meant that.</span>
            </label>
          )}

          {/* Trim / cancel preview — the "explicit sign-off" from item 2 of the change order.
              Always rendered when there's a diff (never silently). If there is nothing to trim
              the preview stays quiet so simple off-hires still feel simple. */}
          {previewErr && <div style={{marginTop:10, padding:8, background:"var(--ach-amber-tint)", color:"var(--ach-amber)", borderRadius:6, fontSize:12}} data-testid="off-hire-preview-err">{previewErr}</div>}
          {preview && trimsOrCancels > 0 && (
            <div style={{marginTop:14, padding:10, background:"var(--ach-page-bg)", borderRadius:6, border:"1px solid var(--ach-amber)"}} data-testid="off-hire-preview">
              <div style={{fontSize:12, fontWeight:600, color:"var(--ach-amber)"}}>Saving this will also:</div>
              <ul style={{margin:"6px 0 0 18px", padding:0, fontSize:12, color:"var(--ach-text-1)", lineHeight:1.6}}>
                {(preview.sd_trims || []).map((sd, i) => (
                  <li key={`sd-${i}`} data-testid={`off-hire-preview-sd-${i}`}>
                    Trim stand-down <strong>{sd.from_date} → {sd.to_date}</strong> back to <strong>{sd.trim_to}</strong>
                    {sd.reason ? ` (${sd.reason})` : ""} — the original end date is preserved.
                  </li>
                ))}
                {(preview.one_off_cancels || []).map((o, i) => (
                  <li key={`oo-${i}`} data-testid={`off-hire-preview-oo-${i}`}>
                    Cancel one-off charge <strong>&quot;{o.description}&quot;</strong> ({money(o.amount)}) scheduled for {o.charge_month}.
                  </li>
                ))}
                {(preview.future_one_offs || preview.custom_charge_cancels || []).map((c, i) => {
                  const kept = keepIds.has(c.id);
                  return (
                    <li key={`cc-${i}`} data-testid={`off-hire-preview-cc-${i}`} style={{listStyle: "none", marginLeft: -18}}>
                      <label style={{display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer"}}>
                        <input type="checkbox" checked={kept} onChange={(e) => {
                          const next = new Set(keepIds);
                          if (e.target.checked) next.add(c.id); else next.delete(c.id);
                          setKeepIds(next);
                        }} data-testid={`off-hire-preview-keep-${c.id}`} style={{marginTop: 3}} />
                        <span style={{textDecoration: kept ? "none" : "line-through", color: kept ? "var(--ach-text-1)" : "var(--ach-text-2)"}}>
                          <strong>&quot;{c.description}&quot;</strong> ({money((c.amount != null ? c.amount : c.rate * (c.qty || 1)))})
                          {" "}scheduled for {c.one_off_month}
                          {kept ? " — kept, will still bill" : " — will be cancelled"}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
              {(preview.demob_charges_to_move || []).length > 0 && (
                <div style={{marginTop: 10, paddingTop: 8, borderTop: "1px dashed var(--ach-amber)", fontSize: 12, color: "var(--ach-text-1)"}} data-testid="off-hire-preview-demob-moves">
                  <div style={{fontWeight: 600}}>Demob charge{preview.demob_charges_to_move.length > 1 ? "s" : ""} from this hire will follow the new off-hire month:</div>
                  <ul style={{margin: "4px 0 0 18px", padding: 0}}>
                    {preview.demob_charges_to_move.map((d, i) => (
                      <li key={`dm-${i}`} data-testid={`off-hire-preview-demob-move-${d.id}`}>
                        <strong>&quot;{d.description}&quot;</strong> ({money((d.amount != null ? d.amount : d.rate * (d.qty || 1)))}) — moving from {d.one_off_month} to {d.will_move_to}.
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <label style={{display:"flex", gap:8, alignItems:"flex-start", marginTop:10, fontSize:12}}>
                <input type="checkbox" checked={acknowledge} onChange={(e)=>setAcknowledge(e.target.checked)} data-testid="off-hire-acknowledge" style={{marginTop:2}} />
                <span>Yes, I want these changes to happen. Cancellations are preserved in the audit trail, not deleted.</span>
              </label>
            </div>
          )}

          {/* Pending bill_at_hire_end charges — editable before they bill.
              Reconstructed 1 Oct 2026 after a regression dropped the UI
              from source. Backend still populates pending_bill_at_hire_end
              on /off-hire/preview and still honours demob_amend_amounts
              on /off-hire. Per Todd's conditions of hire clause (c):
              demob is reconfirmed at off-hire. */}
          {(preview?.pending_bill_at_hire_end || []).length > 0 && (
            <div style={{marginTop:14, padding:12, background:"var(--ach-page-bg)", border:"1px solid var(--ach-blue, #4c7cff)", borderRadius:6}} data-testid="off-hire-pending-demobs">
              <div style={{fontSize:12, fontWeight:600, marginBottom:6}}>Confirm demob charges before they bill</div>
              <div style={{fontSize:11, color:"var(--ach-text-2)", marginBottom:10, fontStyle:"italic"}}>
                Per your conditions of hire clause (c), the demob figure is reconfirmed at time of off-hire. These will bill in {(endDate?.slice(0,7)) || "the off-hire month"}.
              </div>
              {preview.pending_bill_at_hire_end.map((row) => {
                const edited = demobAmends[row.id];
                const value = edited !== undefined ? edited : String(row.current_amount ?? "");
                return (
                  <div key={row.id} data-testid={`off-hire-pending-demob-${row.id}`}
                       style={{display:"grid", gridTemplateColumns:"1fr 140px", gap:8, alignItems:"center", marginBottom:6}}>
                    <div style={{fontSize:12}}>
                      <div><strong>{row.description || "Transport out"}</strong></div>
                      <div style={{color:"var(--ach-text-2)", fontSize:11}}>stored ${Number(row.current_amount || 0).toFixed(2)}</div>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={value}
                      onChange={(e) => setDemobAmends(prev => ({...prev, [row.id]: e.target.value}))}
                      data-testid={`off-hire-pending-demob-input-${row.id}`}
                      style={{padding:"6px 8px", fontSize:13}}
                      placeholder="amount"
                    />
                  </div>
                );
              })}
            </div>
          )}

          {!isAmendment && (
            <div style={{marginTop:14, paddingTop:12, borderTop:"1px solid var(--ach-row-divider)"}}>
              <label style={{display:"flex", alignItems:"center", gap:8, fontSize:13, fontWeight:500, cursor:"pointer"}}>
                <input type="checkbox" checked={addDemob} onChange={(e)=>setAddDemob(e.target.checked)} data-testid="off-hire-add-demob" />
                Add a one-off charge on the way out (e.g. demob, cleaning)
              </label>
              {addDemob && (
                <div className="ach-form-grid" style={{gridTemplateColumns:"2fr 1fr 1fr", gap:8, marginTop:10}} data-testid="off-hire-demob-fields">
                  <div className="ach-field"><label>Description</label>
                    <input value={demob.description} onChange={(e)=>setDemob({...demob, description: e.target.value})} data-testid="off-hire-demob-desc" />
                  </div>
                  <div className="ach-field"><label>Amount ($)</label>
                    <input type="number" min="0" value={demob.rate} onChange={(e)=>setDemob({...demob, rate: e.target.value})} data-testid="off-hire-demob-rate" />
                  </div>
                  <div className="ach-field"><label>Qty</label>
                    <input type="number" min="1" value={demob.qty} onChange={(e)=>setDemob({...demob, qty: e.target.value})} data-testid="off-hire-demob-qty" />
                  </div>
                  <div style={{gridColumn:"1 / -1", fontSize:11, color:"var(--ach-text-2)"}}>
                    Billed as a one-off in the same month as the off-hire ({endDate.slice(0,7)}).
                  </div>
                </div>
              )}
            </div>
          )}
          {err && <div style={{color:"var(--ach-red)", padding:8, background:"var(--ach-red-tint)", borderRadius:6, fontSize:12, marginTop:10}} data-testid="off-hire-err">{err}</div>}
        </div>
        <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
          <button className="ach-btn ach-btn--grey" onClick={onClose} data-testid="off-hire-cancel">Cancel</button>
          <button className="ach-btn ach-btn--primary" onClick={save}
            disabled={busy || !endDate || (isBeforeStart && !confirmBefore) || (needsAck && !acknowledge) || (isAmendment && !hasChangedFromExisting)}
            data-testid="off-hire-go">
            {busy ? "Working…" : (isAmendment ? "Save new date →" : "Off-hire →")}
          </button>
        </div>
      </div>
    </div>
  );
}

function HireInspector({ hire, onClose, onChanged }) {
  const { user } = useAuth();
  const canEditSd = can(user, "hires.stand-downs.edit");
  const canDelSd = can(user, "hires.stand-downs.delete");
  const canOffHireAdjust = can(user, "hires.off-hire.adjust");
  const canReopenHire = can(user, "hires.reopen");
  const canEditHire = can(user, "hires.edit");
  const backdrop = useSafeBackdrop(onClose);
  const [snap, setSnap] = useState(null);
  const [freshHire, setFreshHire] = useState(hire);
  const [editingStart, setEditingStart] = useState(false);
  const [newStart, setNewStart] = useState(hire.start_date || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [reopenBusy, setReopenBusy] = useState(false);
  const [reopenErr, setReopenErr] = useState("");
  const [adjustingOffHire, setAdjustingOffHire] = useState(false);
  const [editingHire, setEditingHire] = useState(false);
  const [ef, setEf] = useState({
    expected_end_date: hire.expected_end_date || "",
    continuous_hire: !!hire.continuous_hire,
    site_location: hire.site_location || "",
    po_number: hire.po_number || "",
    po_expiry_date: hire.po_expiry_date || "",
    transport_provider: hire.transport_provider || "",
    notes: hire.notes || "",
  });
  const [editErr, setEditErr] = useState("");
  const [editBusy, setEditBusy] = useState(false);

  useEffect(() => {
    api.get(`/hires/${hire.id}/charges-snapshot`).then(r => setSnap(r.data)).catch(()=>setSnap({snapshot:[], total_billed:0, has_been_billed:false}));
  }, [hire.id]);

  // Fresh hire fetch — the SD list needs stand-down ids assigned server-side
  // (POST-created SDs get one, and the startup migration backfills legacy ones).
  // Refetched on demand after edit/delete/reopen so numbers refresh in-place.
  const refetchHire = async () => {
    try {
      const r = await api.get(`/hires/${hire.id}`);
      setFreshHire(r.data);
    } catch { /* keep the older copy — the list stays visible */ }
  };
  useEffect(() => { refetchHire(); }, [hire.id]);

  const reopen = async () => {
    if (!window.confirm(`Reopen this hire? ${hire.machine?.unit_number} will go back to on-hire.\n\nThe off-hire date will be cleared.`)) return;
    setReopenBusy(true); setReopenErr("");
    try {
      await api.post(`/hires/${hire.id}/reopen`);
      onChanged();
    } catch (e) { setReopenErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setReopenBusy(false); }
  };
  const saveStart = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/hires/${hire.id}/start-date-change`, { new_start_date: newStart, reason: reason.trim() });
      onChanged();
      setEditingStart(false);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const saveHire = async () => {
    setEditBusy(true); setEditErr("");
    try {
      const payload = {
        expected_end_date: ef.continuous_hire ? null : (ef.expected_end_date || null),
        continuous_hire: !!ef.continuous_hire,
        site_location: ef.site_location || null,
        po_number: ef.po_number || null,
        po_expiry_date: ef.po_expiry_date || null,
        transport_provider: ef.transport_provider || null,
        notes: ef.notes || null,
      };
      await api.patch(`/hires/${hire.id}`, payload);
      onChanged();
      setEditingHire(false);
    } catch (e) { setEditErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setEditBusy(false); }
  };
  const hasBeenBilled = !!snap?.has_been_billed;
  return (
    <div className="ach-modal-backdrop" data-testid="hire-inspector" onMouseDown={backdrop.onMouseDown} onMouseUp={backdrop.onMouseUp}>
      <div className="ach-modal" style={{maxWidth: 720}} {...backdrop.stop}>
        <div className="ach-modal__header">
          <div>
            <h2 style={{margin:0,fontSize:16,fontWeight:600}}>{hire.machine?.unit_number} → {hire.customer?.legal_name}</h2>
            <div style={{fontSize:12, color:"var(--ach-text-2)", marginTop:2}}>
              {hire.site_location || "—"} · started {hire.start_date}{hire.actual_end_date ? ` · off-hired ${hire.actual_end_date}` : (hire.continuous_hire ? " · continuous" : "")}
            </div>
          </div>
          <button className="ach-btn ach-btn--grey" onClick={onClose} style={{padding:"6px 10px"}} data-testid="hi-close">Close</button>
        </div>
        <div className="ach-modal__body">
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14}}>
            <div style={{background:"var(--ach-page-bg)", padding:10, borderRadius:6, fontSize:12}}>
              <div style={{color:"var(--ach-text-2)"}}>Weekly / billing</div>
              <div style={{fontWeight:600}}>{money(hire.weekly_rate)} · {hire.billing_style.replace(/-/g," ")}</div>
              <div style={{color:"var(--ach-text-2)", marginTop:6}}>PO</div>
              <div>{hire.po_number || <em>—</em>}</div>
              <div style={{color:"var(--ach-text-2)", marginTop:6}}>PO expiry</div>
              <div data-testid="hi-po-expiry-display">
                {hire.po_expiry_date
                  ? <>{hire.po_expiry_date} <POExpiryBadge expiry={hire.po_expiry_date} /></>
                  : <em>—</em>}
              </div>
              <div style={{color:"var(--ach-text-2)", marginTop:6}}>Transport provider</div>
              <div>{hire.transport_provider || <em>—</em>}</div>
            </div>
            <div style={{background:"var(--ach-page-bg)", padding:10, borderRadius:6, fontSize:12}}>
              <div style={{color:"var(--ach-text-2)"}}>Start date</div>
              {!editingStart ? (
                <div>
                  <strong>{hire.start_date}</strong>
                  {(!snap?.has_been_billed) && hire.status === "active" && (
                    <button className="ach-btn ach-btn--grey" style={{marginLeft:8, padding:"2px 8px", fontSize:11}} onClick={()=>setEditingStart(true)} data-testid="hi-edit-start">Edit</button>
                  )}
                  {snap?.has_been_billed && (
                    <div style={{fontSize:10, color:"var(--ach-text-2)", marginTop:4}}>Locked — this hire has been billed. Use a rate change or a month adjustment instead.</div>
                  )}
                </div>
              ) : (
                <div>
                  <input type="date" value={newStart} onChange={(e)=>setNewStart(e.target.value)} data-testid="hi-start-input" />
                  <input placeholder="Why is it moving?" value={reason} onChange={(e)=>setReason(e.target.value)} data-testid="hi-start-reason" style={{marginTop:6}} />
                  <div style={{display:"flex", gap:6, marginTop:6}}>
                    <button className="ach-btn ach-btn--primary ach-btn--row" onClick={saveStart} disabled={busy || !newStart || !reason.trim()} data-testid="hi-start-save">Save</button>
                    <button className="ach-btn ach-btn--grey ach-btn--row" onClick={()=>{setEditingStart(false); setErr("");}}>Cancel</button>
                  </div>
                  {err && <div style={{color:"var(--ach-red)", fontSize:11, marginTop:6}}>{err}</div>}
                </div>
              )}
              <div style={{color:"var(--ach-text-2)", marginTop:8}}>Expected off-hire</div>
              <div>{hire.continuous_hire ? <em>continuous</em> : (hire.expected_end_date || "—")}</div>
            </div>
          </div>

          <div style={{marginTop:8, marginBottom:14}}>
            {!editingHire ? (
              <button className="ach-btn ach-btn--grey" style={{padding:"6px 12px", fontSize:12}}
                onClick={()=>setEditingHire(true)} disabled={!canEditHire}
                title={!canEditHire ? "Read-only can't edit hires" : "Correct the PO, site, notes, transport, expected end date or continuous flag. Anything that changes what was billed is frozen and goes through its own tool."}
                data-testid="hi-edit-hire">
                Edit hire details
              </button>
            ) : (
              <div style={{padding:12, background:"var(--ach-page-bg)", borderRadius:6}} data-testid="hi-edit-hire-panel">
                <div className="ach-form-grid" style={{gridTemplateColumns:"1fr 1fr", gap:8}}>
                  <div className="ach-field"><label>Expected off-hire</label>
                    <input type="date" value={ef.expected_end_date} onChange={(e)=>setEf({...ef, expected_end_date: e.target.value})} disabled={ef.continuous_hire} data-testid="hi-eh-expected" />
                    <label style={{display:"flex", gap:6, marginTop:4, fontSize:11}}>
                      <input type="checkbox" checked={ef.continuous_hire} onChange={(e)=>setEf({...ef, continuous_hire: e.target.checked, expected_end_date: e.target.checked ? "" : ef.expected_end_date})} data-testid="hi-eh-continuous" />
                      Continuous / no fixed end
                    </label>
                  </div>
                  <div className="ach-field"><label>Site / location</label>
                    <input value={ef.site_location} onChange={(e)=>setEf({...ef, site_location: e.target.value})} data-testid="hi-eh-site" />
                  </div>
                  <div className="ach-field"><label>Customer PO number</label>
                    <input value={ef.po_number} onChange={(e)=>setEf({...ef, po_number: e.target.value})} data-testid="hi-eh-po" />
                  </div>
                  <div className="ach-field"><label>PO expiry date</label>
                    <input type="date" value={ef.po_expiry_date} onChange={(e)=>setEf({...ef, po_expiry_date: e.target.value})} data-testid="hi-eh-po-expiry" />
                  </div>
                  <div className="ach-field"><label>Transport provider</label>
                    <input value={ef.transport_provider} onChange={(e)=>setEf({...ef, transport_provider: e.target.value})} data-testid="hi-eh-transport-provider" />
                  </div>
                  <div className="ach-field" style={{gridColumn:"1 / -1"}}><label>Notes</label>
                    <textarea rows={2} value={ef.notes} onChange={(e)=>setEf({...ef, notes: e.target.value})} data-testid="hi-eh-notes" />
                  </div>
                </div>
                {editErr && <div style={{color:"var(--ach-red)", fontSize:11, marginTop:6}}>{editErr}</div>}
                <div style={{display:"flex", gap:6, marginTop:10}}>
                  <button className="ach-btn ach-btn--primary" style={{padding:"6px 12px", fontSize:12}} onClick={saveHire} disabled={editBusy} data-testid="hi-eh-save">
                    {editBusy ? "Saving…" : "Save hire changes"}
                  </button>
                  <button className="ach-btn ach-btn--grey" style={{padding:"6px 12px", fontSize:12}} onClick={()=>{setEditingHire(false); setEditErr("");}} data-testid="hi-eh-cancel">Cancel</button>
                </div>
              </div>
            )}
          </div>

          <h3 style={{fontSize:14, fontWeight:600, margin:"14px 0 6px"}} data-testid="hi-sds-heading">
            Stand-downs on this hire
          </h3>
          <div style={{fontSize:11, color:"var(--ach-text-2)", marginBottom:6}}>
            Deducted from the ratio at month-end. Once a stand-down&apos;s month has posted to Xero, editing and removing it are refused server-side — fix on the Xero invoice or record an adjustment instead.
          </div>
          <StandDownsList hire={freshHire} onChanged={()=>{ refetchHire(); onChanged && onChanged(); }} canEdit={canEditSd} canDelete={canDelSd} />

          {/* Off-hire correction controls on an ended hire — only surfaced here so
              the row list stays clean. The gate is server-side; UI is convenience. */}
          {freshHire.status === "ended" && freshHire.actual_end_date && (
            <div style={{marginTop:14, padding:10, background:"var(--ach-page-bg)", borderRadius:6}} data-testid="hi-off-hire-corrections">
              <div style={{fontSize:12, fontWeight:600, marginBottom:6}}>Off-hire correction controls</div>
              <div style={{fontSize:11, color:"var(--ach-text-2)", marginBottom:8}}>
                Off-hired on <strong>{freshHire.actual_end_date}</strong>. If that was wrong, fix it here — as long as the affected months haven&apos;t posted to Xero.
              </div>
              <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
                {canOffHireAdjust && (
                  <button className="ach-btn ach-btn--grey" style={{padding:"4px 10px", fontSize:12}}
                    onClick={()=>setAdjustingOffHire(true)} data-testid="hi-off-hire-adjust">
                    Adjust off-hire date
                  </button>
                )}
                {canReopenHire && (
                  <button className="ach-btn ach-btn--ghost" style={{padding:"4px 10px", fontSize:12}}
                    onClick={reopen} disabled={reopenBusy} data-testid="hi-reopen">
                    {reopenBusy ? "Reopening…" : "Reopen hire"}
                  </button>
                )}
              </div>
              {reopenErr && <div style={{marginTop:8, padding:6, background:"var(--ach-red-tint)", color:"var(--ach-red)", borderRadius:4, fontSize:11}} data-testid="hi-reopen-err">{reopenErr}</div>}
            </div>
          )}

          <h3 style={{fontSize:14, fontWeight:600, margin:"14px 0 6px"}} data-testid="hi-charges-heading">Billing snapshot for this hire</h3>
          <div style={{fontSize:11, color:"var(--ach-text-2)", marginBottom:6}}>Every month-end line generated for this hire — approved lines are locked, drafts still change if month-end is re-run.</div>
          {snap === null ? (
            <div style={{padding:12, fontSize:12, color:"var(--ach-text-2)"}}>Loading…</div>
          ) : snap.snapshot.length === 0 ? (
            <div style={{padding:12, fontSize:12, color:"var(--ach-text-2)", background:"var(--ach-page-bg)", borderRadius:6}}>No month-end lines yet — the first one will appear the next time month-end is generated.</div>
          ) : (
            <table className="ach-table" data-testid="hi-charges-table">
              <thead><tr><th>Month</th><th>Billed?</th><th>Calc</th><th className="money">Amount</th></tr></thead>
              <tbody>
                {snap.snapshot.map((s, i) => (
                  <tr key={i} data-testid={`hi-charge-row-${s.month}`}>
                    <td>{s.month_label || s.month}</td>
                    <td>{s.approved ? <span style={{color:"var(--ach-green)", fontWeight:600}}>Yes</span> : <span style={{color:"var(--ach-amber)", fontWeight:600}}>Draft</span>}</td>
                    <td style={{fontSize:11, color:"var(--ach-text-2)"}}>{s.calculation}</td>
                    <td className="money"><strong>{money(s.amount)}</strong></td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={3} style={{textAlign:"right", fontWeight:600, paddingTop:12}}>Total billed (approved) </td>
                  <td className="money" style={{fontWeight:700}}>{money(snap.total_billed || 0)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
      {adjustingOffHire && (
        <OffHireDialog hire={freshHire} onClose={()=>setAdjustingOffHire(false)}
          onSaved={()=>{ setAdjustingOffHire(false); refetchHire(); onChanged && onChanged(); }} />
      )}
    </div>
  );
}

function ChangeRateDialog({ hire, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [newRate, setNewRate] = useState(String(hire.weekly_rate || ""));
  const [effective, setEffective] = useState(today);
  const [reason, setReason] = useState("");
  const [changeStyle, setChangeStyle] = useState(false);
  const [newStyle, setNewStyle] = useState(hire.billing_style || "standard-monthly");
  const [newCustomDays, setNewCustomDays] = useState(hire.custom_period_days || 17);
  const [newTierAmount, setNewTierAmount] = useState(hire.tier_one_amount || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const backdrop = useSafeBackdrop(onClose);
  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = {
        new_weekly_rate: Number(newRate),
        effective_from: effective,
        reason: reason.trim(),
      };
      if (changeStyle) {
        payload.new_billing_style = newStyle;
        if (newStyle === "custom-period") payload.new_custom_period_days = Number(newCustomDays);
        if (newStyle === "tier-one") payload.new_tier_one_amount = Number(newTierAmount);
      }
      await api.post(`/hires/${hire.id}/rate-change`, payload);
      onSaved(); onClose();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); setBusy(false); }
  };
  const monthlyPreview = Math.round(Number(newRate || 0) * 4.33);
  return (
    <div className="ach-modal-backdrop" data-testid="change-rate-dialog" onMouseDown={backdrop.onMouseDown} onMouseUp={backdrop.onMouseUp}>
      <div className="ach-modal" style={{maxWidth: 620}} {...backdrop.stop}>
        <div className="ach-modal__header"><h2 style={{margin:0,fontSize:16,fontWeight:600}}>Change rate on {hire.machine?.unit_number}</h2></div>
        <div className="ach-modal__body">
          <p style={{margin:"0 0 12px", fontSize:13, color:"var(--ach-text-1)", lineHeight:1.5}}>
            Anything billed <strong>before</strong> the effective date keeps the old rate ({money(hire.weekly_rate)}/week). From the effective date on, the new rate applies. The change and your reason are kept in the machine&apos;s history.
          </p>
          <div className="ach-form-grid">
            <div className="ach-field">
              <label>New weekly rate ($) <span style={{color:"var(--ach-red)"}}>*</span></label>
              <input type="number" min="0" step="50" value={newRate} onChange={(e)=>setNewRate(e.target.value)} data-testid="change-rate-amount" />
              <div className="helper">Monthly worked out: <strong>${monthlyPreview.toLocaleString()}</strong> + GST</div>
            </div>
            <div className="ach-field">
              <label>Effective from <span style={{color:"var(--ach-red)"}}>*</span></label>
              <input type="date" value={effective} onChange={(e)=>setEffective(e.target.value)} data-testid="change-rate-effective" />
              <div className="helper">Must be after the hire started ({shortDate(hire.start_date)}).</div>
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label>Why the change? <span style={{color:"var(--ach-red)"}}>*</span></label>
              <input value={reason} onChange={(e)=>setReason(e.target.value)}
                placeholder="e.g. Extended hire discount agreed with the customer" data-testid="change-rate-reason" />
              <div className="helper">Shown on the machine&apos;s history so anyone looking back knows why.</div>
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label style={{display:"flex", alignItems:"center", gap:8, fontWeight:500, cursor:"pointer"}}>
                <input type="checkbox" checked={changeStyle} onChange={(e)=>setChangeStyle(e.target.checked)} data-testid="change-rate-toggle-style" />
                Also change how it&apos;s billed (currently {hire.billing_style?.replace(/-/g, " ")})
              </label>
            </div>
            {changeStyle && (
              <>
                <div className="ach-field">
                  <label>New billing type</label>
                  <select value={newStyle} onChange={(e)=>setNewStyle(e.target.value)} data-testid="change-rate-style">
                    <option value="tier-one">Tier one (set amount each month)</option>
                    <option value="weekly">Weekly</option>
                    <option value="standard-monthly">Standard monthly (weekly × 4.33)</option>
                    <option value="custom-period">Custom period (choose days)</option>
                  </select>
                </div>
                {newStyle === "custom-period" && (
                  <div className="ach-field">
                    <label>Custom period (days)</label>
                    <input type="number" min="1" value={newCustomDays} onChange={(e)=>setNewCustomDays(e.target.value)} data-testid="change-rate-cp-days" />
                  </div>
                )}
                {newStyle === "tier-one" && (
                  <div className="ach-field">
                    <label>Tier-one monthly amount ($)</label>
                    <input type="number" min="0" value={newTierAmount} onChange={(e)=>setNewTierAmount(e.target.value)} data-testid="change-rate-tier-amount" />
                  </div>
                )}
              </>
            )}
            {err && <div style={{gridColumn:"1 / -1", color:"var(--ach-red)", padding:8, background:"var(--ach-red-tint)", borderRadius:6, fontSize:12}}>{err}</div>}
          </div>
        </div>
        <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
          <button className="ach-btn ach-btn--grey" onClick={onClose} data-testid="change-rate-cancel">Cancel</button>
          <button className="ach-btn ach-btn--primary" onClick={save} disabled={busy || !newRate || !effective || !reason.trim()} data-testid="change-rate-go">
            {busy ? "Saving…" : "Save rate change →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StandDownDialog({ hire, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const backdrop = useSafeBackdrop(onClose);
  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/hires/${hire.id}/stand-downs`, { from_date: from, to_date: to, reason: reason.trim() || null });
      onSaved(); onClose();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); setBusy(false); }
  };
  return (
    <div className="ach-modal-backdrop" data-testid="stand-down-dialog" onMouseDown={backdrop.onMouseDown} onMouseUp={backdrop.onMouseUp}>
      <div className="ach-modal" style={{maxWidth:520}} {...backdrop.stop}>
        <div className="ach-modal__header"><h2 style={{margin:0,fontSize:16,fontWeight:600}}>Log stand-down days on {hire.machine?.unit_number}</h2></div>
        <div className="ach-modal__body">
          <p style={{margin:"0 0 12px", fontSize:13, color:"var(--ach-text-1)", lineHeight:1.5}}>
            Stand-down days are automatically deducted at month-end. Log them here so the calculation matches the operator&apos;s memory.
          </p>
          <div className="ach-form-grid">
            <div className="ach-field"><label>From</label>
              <input type="date" value={from} onChange={(e)=>setFrom(e.target.value)} data-testid="sd-from" />
            </div>
            <div className="ach-field"><label>To</label>
              <input type="date" value={to} onChange={(e)=>setTo(e.target.value)} data-testid="sd-to" />
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label>Reason (optional)</label>
              <input value={reason} onChange={(e)=>setReason(e.target.value)} placeholder="e.g. wet weather / site closure" data-testid="sd-reason" />
            </div>
            {err && <div style={{gridColumn:"1 / -1", color:"var(--ach-red)", padding:8, background:"var(--ach-red-tint)", borderRadius:6, fontSize:12}}>{err}</div>}
          </div>
        </div>
        <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
          <button className="ach-btn ach-btn--grey" onClick={onClose} data-testid="sd-cancel">Cancel</button>
          <button className="ach-btn ach-btn--primary" onClick={save} disabled={busy || !from || !to} data-testid="sd-save">
            {busy ? "Saving…" : "Log stand-down →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// StandDownsList — inline list of the hire's stand-downs with edit / delete
// controls per row (correction controls, 7 Sep 2026, item 2 of the change order).
//
// Server-side gate refuses if the SD's month has posted to Xero. When that
// happens we surface the exact message from the API right on the row rather
// than hiding the button silently — the whole point of the ACH509 fix is that
// dead controls without explanation are the same defect. Rows for pre-id
// stand-downs (rare; legacy) fall back to disabled controls with a note.
function StandDownsList({ hire, onChanged, canEdit, canDelete }) {
  const [sds, setSds] = useState(hire.stand_downs || []);
  const [busyId, setBusyId] = useState(null);
  const [rowErr, setRowErr] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [ef, setEf] = useState({ from_date: "", to_date: "", reason: "" });

  useEffect(() => { setSds(hire.stand_downs || []); }, [hire]);

  const doDelete = async (sd) => {
    if (!sd.id) return;
    if (!window.confirm(`Remove this stand-down (${sd.from_date} → ${sd.to_date})?`)) return;
    setBusyId(sd.id); setRowErr({ ...rowErr, [sd.id]: null });
    try {
      await api.delete(`/hires/${hire.id}/stand-downs/${sd.id}`);
      onChanged();
    } catch (e) {
      setRowErr({ ...rowErr, [sd.id]: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setBusyId(null); }
  };
  const startEdit = (sd) => {
    setEditingId(sd.id);
    setEf({ from_date: sd.from_date, to_date: sd.to_date, reason: sd.reason || "" });
    setRowErr({ ...rowErr, [sd.id]: null });
  };
  const saveEdit = async (sd) => {
    setBusyId(sd.id); setRowErr({ ...rowErr, [sd.id]: null });
    try {
      await api.patch(`/hires/${hire.id}/stand-downs/${sd.id}`, {
        from_date: ef.from_date, to_date: ef.to_date,
        reason: ef.reason.trim() || null,
      });
      setEditingId(null);
      onChanged();
    } catch (e) {
      setRowErr({ ...rowErr, [sd.id]: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setBusyId(null); }
  };

  if (!sds || sds.length === 0) {
    return (
      <div style={{padding:10, background:"var(--ach-page-bg)", borderRadius:6, fontSize:12, color:"var(--ach-text-2)"}} data-testid="hi-sds-empty">
        No stand-downs logged on this hire.
      </div>
    );
  }
  return (
    <div data-testid="hi-sds-list">
      <table className="ach-table">
        <thead><tr>
          <th style={{width:110}}>From</th><th style={{width:110}}>To</th><th>Reason</th><th style={{width:130}}></th>
        </tr></thead>
        <tbody>
        {sds.map((sd, i) => {
          const key = sd.id || `legacy-${i}`;
          const canRowEdit = !!sd.id && canEdit;
          const canRowDelete = !!sd.id && canDelete;
          const isEditing = editingId === sd.id;
          return (
            <React.Fragment key={key}>
              <tr data-testid={`hi-sd-row-${key}`}>
                {!isEditing ? (
                  <>
                    <td data-testid={`hi-sd-from-${key}`}>{sd.from_date}</td>
                    <td data-testid={`hi-sd-to-${key}`}>
                      {sd.to_date}
                      {sd.original_to_date && sd.original_to_date !== sd.to_date && (
                        <div style={{fontSize:10, color:"var(--ach-text-2)"}} title="Trimmed by an off-hire">
                          was {sd.original_to_date}
                        </div>
                      )}
                    </td>
                    <td style={{fontSize:12}}>{sd.reason || <em style={{color:"var(--ach-text-2)"}}>—</em>}</td>
                    <td style={{textAlign:"right"}}>
                      {canRowEdit && (
                        <button className="ach-btn ach-btn--grey ach-btn--row" style={{padding:"2px 8px", fontSize:11, marginRight:4}}
                          onClick={()=>startEdit(sd)} disabled={busyId===sd.id}
                          data-testid={`hi-sd-edit-${key}`}>Edit</button>
                      )}
                      {canRowDelete && (
                        <button className="ach-btn ach-btn--ghost ach-btn--row" style={{padding:"2px 8px", fontSize:11, color:"var(--ach-red)"}}
                          onClick={()=>doDelete(sd)} disabled={busyId===sd.id}
                          data-testid={`hi-sd-delete-${key}`}>Remove</button>
                      )}
                    </td>
                  </>
                ) : (
                  <>
                    <td><input type="date" value={ef.from_date} onChange={(e)=>setEf({...ef, from_date: e.target.value})} data-testid={`hi-sd-edit-from-${key}`} style={{width:"100%"}} /></td>
                    <td><input type="date" value={ef.to_date} onChange={(e)=>setEf({...ef, to_date: e.target.value})} data-testid={`hi-sd-edit-to-${key}`} style={{width:"100%"}} /></td>
                    <td><input value={ef.reason} onChange={(e)=>setEf({...ef, reason: e.target.value})} placeholder="Reason" data-testid={`hi-sd-edit-reason-${key}`} style={{width:"100%"}} /></td>
                    <td style={{textAlign:"right", whiteSpace:"nowrap"}}>
                      <button className="ach-btn ach-btn--primary ach-btn--row" style={{padding:"2px 8px", fontSize:11, marginRight:4}}
                        onClick={()=>saveEdit(sd)} disabled={busyId===sd.id || !ef.from_date || !ef.to_date}
                        data-testid={`hi-sd-edit-save-${key}`}>Save</button>
                      <button className="ach-btn ach-btn--grey ach-btn--row" style={{padding:"2px 8px", fontSize:11}}
                        onClick={()=>{setEditingId(null); setRowErr({...rowErr, [sd.id]: null});}}
                        data-testid={`hi-sd-edit-cancel-${key}`}>Cancel</button>
                    </td>
                  </>
                )}
              </tr>
              {rowErr[sd.id] && (
                <tr data-testid={`hi-sd-err-${key}`}><td colSpan={4} style={{padding:"6px 8px", background:"var(--ach-red-tint)", color:"var(--ach-red)", fontSize:11}}>
                  {rowErr[sd.id]}
                </td></tr>
              )}
              {!sd.id && (
                <tr><td colSpan={4} style={{padding:"4px 8px", fontSize:10, color:"var(--ach-text-2)"}}>
                  Legacy record — edit / remove not available until an admin backfills the id.
                </td></tr>
              )}
            </React.Fragment>
          );
        })}
        </tbody>
      </table>
    </div>
  );
}

function SwapMachineDialog({ hire, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [available, setAvailable] = useState([]);
  const [newMachineId, setNewMachineId] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(today);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const backdrop = useSafeBackdrop(onClose);
  useEffect(() => {
    // Real route is /machines/available/for-hire (server.py:139). The old
    // "/machines/available" matched /machines/{machine_id} with
    // id="available" and 404'd for every role, so this list was always
    // empty. Gated Director/Office/Read-only - Accounts is excluded, which
    // matches canSwapMachine, so Accounts never reaches this dialog.
    api.get("/machines/available/for-hire")
      .then(r => setAvailable(r.data || []))
      .catch(e => {
        setAvailable([]);
        setErr(formatApiErrorDetail(e.response?.data?.detail) ||
               "Could not load the available machines.");
      });
  }, []);
  const save = async () => {
    if (!newMachineId) { setErr("Pick the replacement machine"); return; }
    if (!reason.trim()) { setErr("Give a reason for the swap"); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/hires/${hire.id}/swap-machine`, {
        new_machine_id: newMachineId, effective_date: effectiveDate, reason: reason.trim(),
      });
      onSaved(); onClose();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); setBusy(false); }
  };
  return (
    <div className="ach-modal-backdrop" data-testid="swap-machine-dialog" onMouseDown={backdrop.onMouseDown} onMouseUp={backdrop.onMouseUp}>
      <div className="ach-modal" style={{maxWidth:520}} {...backdrop.stop}>
        <div className="ach-modal__header"><h2 style={{margin:0,fontSize:16,fontWeight:600}}>Swap the machine on {hire.machine?.unit_number}'s job</h2></div>
        <div className="ach-modal__body">
          <p style={{margin:"0 0 12px", fontSize:13, color:"var(--ach-text-1)", lineHeight:1.5}}>
            {hire.customer?.legal_name} and the rate stay exactly the same — only the machine changes, from the date below. Invoices will show each machine against its own dates.
          </p>
          <div className="ach-form-grid">
            <div className="ach-field" style={{gridColumn:"1 / -1"}}><label>Replacement machine (must be free)</label>
              <select value={newMachineId} onChange={(e)=>setNewMachineId(e.target.value)} data-testid="swap-new-machine">
                <option value="">— pick a machine —</option>
                {available.map(m => (
                  <option key={m.id} value={m.id}>{m.unit_number} · {m.make} {m.model_name} · {m.capacity_tonnes}t</option>
                ))}
              </select>
            </div>
            <div className="ach-field"><label>Swap takes effect from</label>
              <input type="date" value={effectiveDate} onChange={(e)=>setEffectiveDate(e.target.value)} data-testid="swap-effective-date" />
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label>Reason</label>
              <input value={reason} onChange={(e)=>setReason(e.target.value)} placeholder="e.g. ACH505 needed elsewhere, swapped for ACH105" data-testid="swap-reason" />
            </div>
            {err && <div style={{gridColumn:"1 / -1", color:"var(--ach-red)", padding:8, background:"var(--ach-red-tint)", borderRadius:6, fontSize:12}}>{err}</div>}
          </div>
        </div>
        <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
          <button className="ach-btn ach-btn--grey" onClick={onClose} data-testid="swap-cancel">Cancel</button>
          <button className="ach-btn ach-btn--primary" onClick={save} disabled={busy || !newMachineId || !effectiveDate} data-testid="swap-save">
            {busy ? "Swapping…" : "Swap machine →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// HireCommentCell — compact comments icon with count + hover tooltip of the
// latest comment. Clicking opens the full history dialog. Rendered inside the
// hire row; stops propagation so clicking the cell doesn't open the inspector.
// Added 1 Oct 2026 (Todd: "add a comments section to hire jobs that everyone
// can easily read, hovering shows latest, clicking shows full history").
// Deleted comments (soft-delete, Todd 1 Oct 2026 late) are filtered OUT of
// both the count and the latest-hover — a comment Todd removed is one he
// doesn't want on screen; it still lives in the DB for the audit trail.
function HireCommentCell({ hire, onOpen }) {
  const allComments = hire.comments || [];
  const visible = allComments.filter(c => !c.deleted_at);
  const count = visible.length;
  const latest = count > 0 ? visible[visible.length - 1] : null;
  const title = latest
    ? `Latest by ${latest.author_name}, ${relTime(latest.created_at)}:\n${latest.text}`
    : "No comments yet — click to add one";
  return (
    <td data-testid={`hire-comments-cell-${hire.machine?.unit_number}`}
        style={{textAlign:"center", padding:"4px 6px"}}
        onClick={(e)=>e.stopPropagation()}
        title={title}>
      <button
        type="button"
        className="ach-btn ach-btn--ghost"
        style={{padding:"4px 8px", fontSize:12, display:"inline-flex", alignItems:"center", gap:4,
          color: count > 0 ? "var(--ach-text-1)" : "var(--ach-text-2)",
          fontWeight: count > 0 ? 600 : 400}}
        onClick={(e)=>{ e.stopPropagation(); onOpen(); }}
        data-testid={`hire-comments-btn-${hire.machine?.unit_number}`}>
        <MessageSquare size={14} />
        {count > 0 ? count : ""}
      </button>
    </td>
  );
}

// Relative-time formatter — "2 days ago", "just now", etc. Exact timestamp is
// shown in the dialog's tooltip (title attribute). Days > 30 fall back to the
// absolute date so an old comment doesn't say "87 days ago" when "4 Jul 2026"
// is more useful.
function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const secs = Math.floor((Date.now() - t) / 1000);
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs/60)} min ago`;
  if (secs < 86400) return `${Math.floor(secs/3600)} hr ago`;
  const days = Math.floor(secs / 86400);
  if (days < 30) return days === 1 ? "yesterday" : `${days} days ago`;
  return new Date(iso).toLocaleDateString("en-AU", {day:"numeric", month:"short", year:"numeric"});
}

// PO expiry classifier — single source of truth for "is this PO flagged".
// Matches backend/server.py dashboard-KPI logic (days_to(po_expiry_date) <= 30
// including negative). Used by the dashboard filter and the row badge so
// what the tile counts is the same set as what the row shows.
function poExpiryStatus(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.floor((d.getTime() - today.getTime()) / 86400000);
  if (days < 0) return { days, tone: "red", label: `PO expired ${Math.abs(days)}d ago` };
  if (days === 0) return { days, tone: "red", label: "PO expires today" };
  if (days <= 30) return { days, tone: "amber", label: `PO exp in ${days}d` };
  return { days, tone: "neutral", label: `PO exp ${dateStr}` };
}

// PO expiry badge — only renders when the PO is within 30 days or already
// expired, matching the dashboard flag rule. Appears inline on the hire row
// so the girls see it where the work happens, not only on the dashboard.
function POExpiryBadge({ expiry }) {
  const s = poExpiryStatus(expiry);
  if (!s || s.tone === "neutral") return null;
  const bg = s.tone === "red" ? "var(--ach-red-tint)" : "var(--ach-amber-tint, #fff4d6)";
  const fg = s.tone === "red" ? "var(--ach-red)" : "var(--ach-amber, #b25b00)";
  return (
    <span data-testid="po-expiry-badge"
          style={{display:"inline-block", padding:"1px 6px", borderRadius:4,
            fontSize:10, fontWeight:600, background:bg, color:fg,
            whiteSpace:"nowrap", marginLeft:4}}>
      {s.label}
    </span>
  );
}

// HireCommentsDialog — full comment history (newest first), each row shows
// text, author name, relative time (exact timestamp in the hover tooltip).
// Append-only in the DB — delete is a soft-delete that sets deleted_at /
// deleted_by on the comment; the row stays so the audit trail is intact.
// Office/Accounts can delete their own; Director can delete any; Read-only
// cannot delete (same gate as can't add).
// "Show deleted" toggle — Director-only — reveals soft-deleted rows with
// strikethrough so a lead can recover the history if needed.
function HireCommentsDialog({ hire, canAdd, canDeleteOwn, canDeleteAny, currentUserId, onClose, onSaved }) {
  const [comments, setComments] = useState(hire.comments || []);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const backdrop = useSafeBackdrop(onClose);
  useEffect(() => { setComments(hire.comments || []); }, [hire]);
  const add = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/hires/${hire.id}/comments`, { text: t });
      setComments([...(comments || []), r.data.comment]);
      setText("");
      onSaved && onSaved();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };
  const removeComment = async (c) => {
    if (!window.confirm(`Delete this comment?\n\n"${c.text.slice(0, 160)}${c.text.length > 160 ? '…' : ''}"\n\nIt will be hidden from the list, but the record stays in our audit log.`)) return;
    setDeletingId(c.id); setErr("");
    try {
      const r = await api.delete(`/hires/${hire.id}/comments/${c.id}`);
      // Patch in the deleted_at from the response so UI updates immediately
      setComments(prev => prev.map(x => x.id === c.id ? {...x,
        deleted_at: r.data.deleted_at,
        deleted_by: currentUserId,
        deleted_by_name: "You"} : x));
      onSaved && onSaved();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setDeletingId(null);
  };
  const canRemove = (c) => {
    if (c.deleted_at) return false;
    if (canDeleteAny) return true;
    if (canDeleteOwn && c.author_id === currentUserId) return true;
    return false;
  };
  const allSorted = [...comments].sort((a,b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const visible = showDeleted ? allSorted : allSorted.filter(c => !c.deleted_at);
  const hiddenDeletedCount = allSorted.filter(c => c.deleted_at).length;
  return (
    <div className="ach-modal-backdrop" data-testid="hire-comments-dialog"
         onMouseDown={backdrop.onMouseDown} onMouseUp={backdrop.onMouseUp}>
      <div className="ach-modal" style={{maxWidth:560}} {...backdrop.stop}>
        <div className="ach-modal__header">
          <h2 style={{margin:0, fontSize:16, fontWeight:600}}>
            Comments on {hire.machine?.unit_number} — {hire.customer?.legal_name}
          </h2>
        </div>
        <div className="ach-modal__body">
          {canDeleteAny && hiddenDeletedCount > 0 && (
            <div style={{fontSize:12, color:"var(--ach-text-2)", marginBottom:8, display:"flex", gap:6, alignItems:"center"}}>
              <input type="checkbox" id="hire-comments-show-deleted"
                     checked={showDeleted}
                     onChange={(e)=>setShowDeleted(e.target.checked)}
                     data-testid="hire-comments-show-deleted-toggle" />
              <label htmlFor="hire-comments-show-deleted" style={{cursor:"pointer"}}>
                Show deleted ({hiddenDeletedCount})
              </label>
            </div>
          )}
          {visible.length === 0 && (
            <div style={{padding:16, textAlign:"center", color:"var(--ach-text-2)", fontSize:13}}
                 data-testid="hire-comments-empty">
              {hiddenDeletedCount > 0
                ? "No comments visible. Toggle 'Show deleted' to see removed ones."
                : "No comments yet."}
            </div>
          )}
          {visible.length > 0 && (
            <div style={{maxHeight:360, overflowY:"auto", marginBottom:12}}
                 data-testid="hire-comments-list">
              {visible.map(c => {
                const deleted = !!c.deleted_at;
                return (
                <div key={c.id}
                     data-testid={`hire-comment-${c.id}`}
                     style={{padding:"10px 12px", borderBottom:"1px solid var(--ach-card-border)",
                       display:"flex", gap:8, alignItems:"flex-start",
                       opacity: deleted ? 0.55 : 1}}>
                  <div style={{flex:1}}>
                    <div style={{whiteSpace:"pre-wrap", fontSize:13, lineHeight:1.45,
                        color:"var(--ach-text-1)",
                        textDecoration: deleted ? "line-through" : "none"}}>
                      {c.text}
                    </div>
                    <div style={{marginTop:6, fontSize:11, color:"var(--ach-text-2)"}}
                         title={new Date(c.created_at).toLocaleString("en-AU")}>
                      <strong style={{color:"var(--ach-text-1)"}}>{c.author_name}</strong>
                      {" · "}
                      <span>{relTime(c.created_at)}</span>
                      {deleted && (
                        <span data-testid={`hire-comment-${c.id}-deleted-tag`}
                              style={{marginLeft:8, color:"var(--ach-red)"}}>
                          deleted by {c.deleted_by_name || "someone"} · {relTime(c.deleted_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  {canRemove(c) && (
                    <button type="button"
                            className="ach-btn ach-btn--ghost"
                            style={{padding:"4px 6px", color:"var(--ach-red)"}}
                            disabled={deletingId === c.id}
                            onClick={()=>removeComment(c)}
                            title="Delete this comment"
                            data-testid={`hire-comment-${c.id}-delete`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              );})}
            </div>
          )}
          {canAdd ? (
            <div className="ach-field">
              <label>Add a comment</label>
              <textarea value={text} onChange={(e)=>setText(e.target.value)}
                data-testid="hire-comment-input"
                placeholder="e.g. Site shut down Thursday — crane idle. Called Sam, back on Monday."
                style={{width:"100%", minHeight:72, resize:"vertical", padding:8, fontSize:13,
                  border:"1px solid var(--ach-card-border)", borderRadius:6, fontFamily:"inherit"}} />
            </div>
          ) : (
            <div style={{padding:10, fontSize:12, color:"var(--ach-text-2)",
                background:"var(--ach-card-bg-subtle)", borderRadius:6}}
                 data-testid="hire-comments-readonly">
              You can read every comment on this hire but your role doesn&apos;t allow adding new ones.
            </div>
          )}
          {err && <div style={{marginTop:8, color:"var(--ach-red)", padding:8,
            background:"var(--ach-red-tint)", borderRadius:6, fontSize:12}}
            data-testid="hire-comment-error">{err}</div>}
        </div>
        <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
          <button className="ach-btn ach-btn--grey" onClick={onClose} data-testid="hire-comments-close">Close</button>
          {canAdd && (
            <button className="ach-btn ach-btn--primary" onClick={add}
                    disabled={busy || !text.trim()}
                    data-testid="hire-comment-save">
              {busy ? "Posting…" : "Post comment"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}



export default function Hires() {
  const [hires, setHires] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [tab, setTab] = useState("active");
  const [offHiring, setOffHiring] = useState(null);
  const [changingRate, setChangingRate] = useState(null);
  const [loggingStandDown, setLoggingStandDown] = useState(null);
  const [swappingMachine, setSwappingMachine] = useState(null);
  const [inspecting, setInspecting] = useState(null);
  const [commentingOn, setCommentingOn] = useState(null);
  const [q, setQ] = useState("");
  const [flash, setFlash] = useState(null);
  // Column sorting (Friction Batch f, Sep 2026 — extracted into shared hook
  // in Sep 2026 evening when we added the same behaviour to Machines and
  // Customers). Same interaction contract everywhere: click asc, click again
  // desc, empties last. See src/lib/sortable.js.
  const { sort, toggleSort, sortArrow, applySort } = useSortable();
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  const load = () => Promise.all([
    api.get("/hires"),
    api.get("/drafts?kind=hire").catch(()=>({data:{drafts:[]}})),
  ]).then(([h, d]) => {
    setHires(h.data);
    setDrafts(d.data.drafts || []);
  });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const hi = searchParams.get("highlight") || searchParams.get("edit");
    if (hi && hires.length) {
      const h = hires.find(x => x.id === hi);
      if (h) setInspecting(h);
    }
  }, [searchParams, hires]);

  const active = hires.filter(h => h.status === "active");
  const ended = hires.filter(h => h.status === "ended");
  const rows = tab === "active" ? active : (tab === "ended" ? ended : []);
  // ?filter=po-expiring forces the view to only hires whose PO is within 30
  // days or already expired — the dashboard tile links here so clicking the
  // count lands on the exact set that was counted. Deliberately kept on the
  // "active" tab, because the dashboard KPI itself only counts active hires.
  const poFilterActive = searchParams.get("filter") === "po-expiring";
  const filteredRows = rows.filter(h => {
    if (poFilterActive) {
      const s = poExpiryStatus(h.po_expiry_date);
      if (!s || s.tone === "neutral") return false;
    }
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (h.machine?.unit_number || "").toLowerCase().includes(s) ||
      (h.machine?.make || "").toLowerCase().includes(s) ||
      (h.machine?.model_name || "").toLowerCase().includes(s) ||
      (h.customer?.legal_name || "").toLowerCase().includes(s) ||
      (h.site_location || "").toLowerCase().includes(s) ||
      (h.po_number || "").toLowerCase().includes(s)
    );
  });
  // Sort AFTER filter so the user's search narrows to what they can see
  // and the sort then orders that. Per-column value getter passed to the
  // shared applySort helper (see src/lib/sortable.js).
  const sortedRows = applySort(filteredRows, (h, key) => {
    switch (key) {
      case "machine":  return unitNumberKey(h.machine?.unit_number);
      case "customer": return (h.customer?.legal_name || "").toLowerCase();
      case "site":     return (h.site_location || "").toLowerCase();
      case "started":  return h.start_date || "";
      case "backby":
        if (h.continuous_hire) return null;  // continuous hires sort last
        return (tab === "active" ? h.expected_end_date : h.actual_end_date) || "";
      case "weekly":   // fallthrough — monthly is weekly × 4.33, same order
      case "monthly":  return Number(h.weekly_rate) || 0;
      case "billing":  return (h.billing_style || "").toLowerCase();
      default:         return "";
    }
  });
  const filteredDrafts = drafts.filter(d => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (d.title || "").toLowerCase().includes(s);
  });

  const deleteDraft = async (id) => {
    if (!window.confirm("Delete this saved-for-later draft?")) return;
    await api.delete(`/drafts/${id}`);
    load();
  };

  const canOffHire = can(user, "hires.off-hire");
  const canStandDown = can(user, "hires.stand-downs");
  // Swapping the machine on a job is fleet work and the server does not
  // allow it for Accounts (server.py:3168). It previously rode on
  // canOffHire, which would have exposed a button that 403s.
  const canSwapMachine = can(user, "hires.swap-machine");
  const canChangeRate = can(user, "hires.rate-change");
  const canAddComment = can(user, "hires.comments.add");
  const canDeleteOwnComment = can(user, "hires.comments.delete-own");
  const canDeleteAnyComment = can(user, "hires.comments.delete-any");
  const showFlash = (tone, text) => { setFlash({tone, text}); setTimeout(()=>setFlash(null), 4000); };

  return (
    <div data-testid="hires-page">
      <PageHeader
        title="Hires"
        subtitle="Everything that's out — and everything that's come back"
        actions={
          <button className="ach-btn ach-btn--primary" onClick={()=>nav("/start-a-hire")} data-testid="btn-start-hire-header">+ Start a hire</button>
        }
      />

      <div className="ach-kpi-row" style={{gridTemplateColumns:"repeat(3, minmax(0,1fr))"}}>
        <KpiCard tone="green" number={active.length} label="Out on hire right now" testid="hires-kpi-active" />
        <KpiCard tone="neutral" number={ended.length} label="Finished — kept for records" testid="hires-kpi-ended" />
        <KpiCard tone="maroon" number={money0(active.reduce((s,h)=>s+(h.weekly_rate*4.33),0))} label="Weekly × 4.33 · all active" testid="hires-kpi-run-rate" />
      </div>

      <div className="ach-card">
        <div style={{display:"flex", gap: 8, marginBottom: 14, alignItems:"center", flexWrap:"wrap"}}>
          <button className={`ach-btn ${tab==="active" ? "ach-btn--primary" : "ach-btn--grey"}`} onClick={()=>setTab("active")} data-testid="tab-active-hires">On hire ({active.length})</button>
          <button className={`ach-btn ${tab==="ended" ? "ach-btn--primary" : "ach-btn--grey"}`} onClick={()=>setTab("ended")} data-testid="tab-ended-hires">Finished ({ended.length})</button>
          <button className={`ach-btn ${tab==="drafts" ? "ach-btn--primary" : "ach-btn--grey"}`} onClick={()=>setTab("drafts")} data-testid="tab-hire-drafts">Drafts ({drafts.length})</button>
          <div style={{flex:1}} />
          <input placeholder="Search unit, model, customer, site…" value={q} onChange={(e)=>setQ(e.target.value)}
            data-testid="hires-search"
            style={{border:"1px solid var(--ach-card-border)", borderRadius:6, padding:"8px 12px", fontSize:13, width: 280, maxWidth:"100%", boxSizing:"border-box"}} />
        </div>
        {poFilterActive && (
          <div data-testid="po-expiring-filter-chip"
               style={{display:"inline-flex", alignItems:"center", gap:8,
                 marginBottom:10, padding:"6px 10px", borderRadius:6,
                 background:"var(--ach-amber-tint, #fff4d6)",
                 color:"var(--ach-amber, #b25b00)", fontSize:12, fontWeight:600}}>
            <span>Filter: POs expiring within 30 days (or already expired)</span>
            <button className="ach-btn ach-btn--ghost"
                    style={{padding:"2px 6px", fontSize:11, color:"inherit"}}
                    onClick={()=>nav("/hires")}
                    data-testid="po-expiring-filter-clear">Clear ×</button>
          </div>
        )}
        {tab === "drafts" ? (
          <div className="ach-table-wrap" data-testid="hire-drafts-list">
            {filteredDrafts.length === 0 ? (
              <div style={{padding:20, textAlign:"center", color:"var(--ach-text-2)", fontSize:13}}>
                No saved-for-later hire drafts. When you tap <em>Save draft for later</em> on Start-a-Hire, it&apos;ll appear here.
              </div>
            ) : (
              <table className="ach-table">
                <thead>
                  <tr>
                    <th>Draft</th>
                    <th>Saved by</th>
                    <th>Updated</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDrafts.map(d => (
                    <tr key={d.id} data-testid={`draft-hire-${d.id}`}>
                      <td><div className="unit-num">{d.title}</div><div className="subtitle">saved {new Date(d.created_at).toLocaleDateString("en-AU")}</div></td>
                      <td>{d.created_by_name}</td>
                      <td>{new Date(d.updated_at).toLocaleDateString("en-AU")}</td>
                      <RowActions>
                        <button className="ach-btn ach-btn--grey ach-btn--row" onClick={()=>nav(`/start-a-hire?draft=${d.id}`)} data-testid={`draft-hire-resume-${d.id}`}>Resume →</button>
                        <button className="ach-btn ach-btn--ghost ach-btn--row" style={{color:"var(--ach-red)"}} onClick={()=>deleteDraft(d.id)} data-testid={`draft-hire-delete-${d.id}`}>×</button>
                      </RowActions>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
        <div className="ach-table-wrap">
          <table className="ach-table">
            <thead>
              <tr>
                {/* Sortable columns (Friction Batch f). `data-testid`s let a QA
                    or a screen reader user target headers precisely. */}
                <th onClick={()=>toggleSort("machine")}  style={{cursor:"pointer", userSelect:"none"}} data-testid="hire-col-sort-machine">Machine{sortArrow("machine")}</th>
                <th onClick={()=>toggleSort("customer")} style={{cursor:"pointer", userSelect:"none"}} data-testid="hire-col-sort-customer">Customer{sortArrow("customer")}</th>
                <th onClick={()=>toggleSort("site")}     style={{cursor:"pointer", userSelect:"none"}} data-testid="hire-col-sort-site">Site{sortArrow("site")}</th>
                <th onClick={()=>toggleSort("started")}  style={{cursor:"pointer", userSelect:"none"}} data-testid="hire-col-sort-started">Started{sortArrow("started")}</th>
                <th onClick={()=>toggleSort("backby")}   style={{cursor:"pointer", userSelect:"none"}} data-testid="hire-col-sort-backby">{tab === "active" ? "Expected back" : "Off-hired"}{sortArrow("backby")}</th>
                <th onClick={()=>toggleSort("weekly")}   style={{cursor:"pointer", userSelect:"none"}} data-testid="hire-col-sort-weekly">Weekly rate{sortArrow("weekly")}</th>
                <th onClick={()=>toggleSort("monthly")}  className="money" style={{cursor:"pointer", userSelect:"none"}} data-testid="hire-col-sort-monthly">Monthly{sortArrow("monthly")}</th>
                <th onClick={()=>toggleSort("billing")}  style={{cursor:"pointer", userSelect:"none"}} data-testid="hire-col-sort-billing">How it&apos;s billed{sortArrow("billing")}</th>
                <th style={{width:60, textAlign:"center"}} data-testid="hire-col-comments" title="Internal comments on this hire"><MessageSquare size={14} style={{verticalAlign:"middle"}} /></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map(h => (
                <tr key={h.id} data-testid={`hire-row-${h.machine?.unit_number}`}
                    onClick={(e)=>{ if (e.target.tagName !== "BUTTON") setInspecting(h); }}
                    style={{cursor:"pointer"}}>
                  <td><div className="unit-num">{h.machine?.unit_number}</div><div className="subtitle">{h.machine?.make} {h.machine?.model_name}</div></td>
                  <td>{h.customer?.legal_name}</td>
                  <td>{h.site_location || "—"}</td>
                  <td>{shortDate(h.start_date)}</td>
                  <td>{h.continuous_hire && !h.actual_end_date
                        ? <em style={{color:"var(--ach-text-2)"}}>continuous</em>
                        : (tab === "active" ? shortDate(h.expected_end_date) : shortDate(h.actual_end_date))}
                      {h.po_expiry_date && (
                        <div style={{marginTop:2}}><POExpiryBadge expiry={h.po_expiry_date} /></div>
                      )}
                  </td>
                  <td>
                    {money(h.weekly_rate)}
                    {(h.rate_history || []).length > 0 && (
                      <div style={{fontSize:10, color:"var(--ach-amber)", marginTop:2}} title="Rate has changed at least once">↺ rate changed</div>
                    )}
                  </td>
                  <td className="money">{money0(h.weekly_rate * 4.33)}</td>
                  <td><StatusPill tone="neutral">{h.billing_style.replace(/-/g, " ").toUpperCase()}</StatusPill></td>
                  <HireCommentCell hire={h} onOpen={()=>setCommentingOn(h)} />
                  <RowActions>
                    {tab === "active" && canChangeRate && (
                      <button className="ach-btn ach-btn--grey ach-btn--row" onClick={(e)=>{e.stopPropagation(); setChangingRate(h);}} data-testid={`change-rate-${h.machine?.unit_number}`}>Change rate</button>
                    )}
                    {tab === "active" && canStandDown && (
                      <button className="ach-btn ach-btn--grey ach-btn--row" onClick={(e)=>{e.stopPropagation(); setLoggingStandDown(h);}} data-testid={`stand-down-${h.machine?.unit_number}`}>Log stand-down</button>
                    )}
                    {tab === "active" && canSwapMachine && (
                      <button className="ach-btn ach-btn--grey ach-btn--row" onClick={(e)=>{e.stopPropagation(); setSwappingMachine(h);}} data-testid={`swap-machine-${h.machine?.unit_number}`}>Swap machine</button>
                    )}
                    {tab === "active" && canOffHire && (
                      <button className="ach-btn ach-btn--ghost ach-btn--row" onClick={(e)=>{e.stopPropagation(); setOffHiring(h);}} data-testid={`off-hire-${h.machine?.unit_number}`}>Off-hire →</button>
                    )}
                    {tab === "active" && user?.role === "Director" && (
                      <button className="ach-btn ach-btn--ghost ach-btn--row" style={{color:"var(--ach-red)"}} onClick={async (e)=>{
                          e.stopPropagation();
                          if (!window.confirm(`Delete this hire (${h.machine?.unit_number} → ${h.customer?.legal_name})?\n\nOnly allowed before the hire has been billed.`)) return;
                          try {
                            await api.delete(`/hires/${h.id}`);
                            showFlash("green", `Deleted ${h.machine?.unit_number} hire.`);
                            load();
                          } catch (err) { showFlash("red", formatApiErrorDetail(err.response?.data?.detail) || err.message); }
                        }}
                        data-testid={`delete-hire-${h.machine?.unit_number}`}>Delete</button>
                    )}
                  </RowActions>
                </tr>
              ))}
              {sortedRows.length === 0 && (
                <tr><td colSpan={10} style={{textAlign:"center", padding: 24, color: "var(--ach-text-2)"}}>Nothing here.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {offHiring && <OffHireDialog hire={offHiring} onClose={()=>setOffHiring(null)}
        onSaved={()=>{ showFlash("green", `${offHiring.machine?.unit_number} is now off-hire and back in the yard.`); load(); }} />}
      {changingRate && <ChangeRateDialog hire={changingRate} onClose={()=>setChangingRate(null)}
        onSaved={()=>{ showFlash("green", `Rate change saved on ${changingRate.machine?.unit_number}.`); load(); }} />}
      {loggingStandDown && <StandDownDialog hire={loggingStandDown} onClose={()=>setLoggingStandDown(null)}
        onSaved={()=>{ showFlash("green", `Stand-down logged on ${loggingStandDown.machine?.unit_number}.`); load(); }} />}
      {swappingMachine && <SwapMachineDialog hire={swappingMachine} onClose={()=>setSwappingMachine(null)}
        onSaved={()=>{ showFlash("green", `Machine swapped on ${swappingMachine.customer?.legal_name}'s job.`); load(); }} />}
      {inspecting && <HireInspector hire={inspecting} onClose={()=>setInspecting(null)}
        onChanged={()=>{ showFlash("green", `Updated ${inspecting.machine?.unit_number}.`); load(); setInspecting(null); }} />}
      {commentingOn && <HireCommentsDialog hire={commentingOn}
        canAdd={canAddComment}
        canDeleteOwn={canDeleteOwnComment}
        canDeleteAny={canDeleteAnyComment}
        currentUserId={user?.id}
        onClose={()=>setCommentingOn(null)}
        onSaved={()=>{ showFlash("green", `Updated comments on ${commentingOn.machine?.unit_number}.`); load(); }} />}

      {flash && (
        <div data-testid="hires-flash"
          style={{position:"fixed", right:20, bottom:20, zIndex:1000, padding:"10px 14px", borderRadius:8,
            background: flash.tone === "red" ? "var(--ach-red-tint)" : "var(--ach-green-tint)",
            color: flash.tone === "red" ? "var(--ach-red)" : "var(--ach-green)",
            border:"1px solid var(--ach-card-border)", fontSize:13, fontWeight:500, maxWidth:360, boxShadow:"0 4px 16px rgba(0,0,0,0.08)"}}>
          {flash.text}
        </div>
      )}
    </div>
  );
}
